import { useEffect } from "react";
import { Label } from "@groot/ui/label";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@groot/ui/button";
import { Input } from "@groot/ui/input";
import { NumberInput } from "@/components/number-input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@groot/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@groot/ui/select";
import { toast } from "sonner";
import { useCreateProduct, useUpdateProduct } from "@/pages/product/hooks/useProducts";
import type { Product } from "@/pages/product/hooks/useProducts";

const productSchema = z.object({
  name: z.string().min(1, "Name is required"),
  size: z.enum(["A5", "A4", "Custom"]),
  unitPrice: z.coerce.number().positive("Unit price must be positive"),
  printFormat: z.enum(["mini", "medium", "large"]).nullable(),
});

const NOT_POLAROID = "none";

type ProductFormValues = z.infer<typeof productSchema>;

interface ProductFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When provided, the dialog edits this product. Otherwise it creates a new one. */
  product?: Product | null;
}

export function ProductFormDialog({ open, onOpenChange, product }: ProductFormDialogProps) {
  const isEdit = Boolean(product);
  const createProduct = useCreateProduct();
  const updateProduct = useUpdateProduct();

  const form = useForm<z.input<typeof productSchema>, unknown, ProductFormValues>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      name: "",
      size: "A4",
      unitPrice: 0,
      printFormat: null,
    },
  });

  // Populate form values when opening (edit mode) and reset when closing.
  useEffect(() => {
    if (open) {
      form.reset(
        product
          ? {
              name: product.name,
              size: product.size,
              unitPrice: Number(product.unitPrice),
              printFormat: product.printFormat ?? null,
            }
          : { name: "", size: "A4", unitPrice: 0, printFormat: null },
      );
    }
  }, [open, product, form]);

  const onSubmit = async (values: ProductFormValues) => {
    try {
      if (isEdit && product) {
        await updateProduct.mutateAsync({ id: product.id, data: values });
        toast.success("Success", { description: "Product updated" });
      } else {
        await createProduct.mutateAsync(values);
        toast.success("Success", { description: "Product created" });
      }
      onOpenChange(false);
    } catch (error) {
      console.error(error);
      toast.error("Error", {
        description: isEdit ? "Unable to update product" : "Unable to create product",
      });
    }
  };

  const isPending = createProduct.isPending || updateProduct.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Product" : "Add Product"}</DialogTitle>
        </DialogHeader>
        <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
          <div className="space-y-2">
            <Label>Name</Label>
            <Input placeholder="Product name" {...form.register("name")} />
            {form.formState.errors.name && (
              <p className="text-sm text-destructive">{form.formState.errors.name.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label>Size</Label>
            <Select
              value={form.watch("size")}
              onValueChange={(value) => form.setValue("size", value as "A5" | "A4" | "Custom")}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select size" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="A5">A5</SelectItem>
                <SelectItem value="A4">A4</SelectItem>
                <SelectItem value="Custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Polaroid size</Label>
            <Select
              value={form.watch("printFormat") ?? NOT_POLAROID}
              onValueChange={(value) =>
                form.setValue(
                  "printFormat",
                  value === NOT_POLAROID ? null : (value as "mini" | "medium" | "large"),
                )
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NOT_POLAROID}>Not a polaroid</SelectItem>
                <SelectItem value="mini">Mini · 2 × 2.5″</SelectItem>
                <SelectItem value="medium">Medium · 2.5 × 3″</SelectItem>
                <SelectItem value="large">Large · 3 × 4.5″</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Orders of this product are laid out at this size on the print canvas.
            </p>
          </div>
          <div className="space-y-2">
            <Label>Unit Price (₹)</Label>
            <Controller
              control={form.control}
              name="unitPrice"
              render={({ field }) => (
                <NumberInput
                  placeholder="0.00"
                  value={field.value as number}
                  onValueChange={field.onChange}
                  onBlur={field.onBlur}
                />
              )}
            />
            {form.formState.errors.unitPrice && (
              <p className="text-sm text-destructive">{form.formState.errors.unitPrice.message}</p>
            )}
          </div>
          <Button className="w-full" type="submit" disabled={isPending}>
            {isPending
              ? isEdit
                ? "Saving..."
                : "Creating..."
              : isEdit
                ? "Save Changes"
                : "Create"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
